import { cloneObject, formatDateTimeWithSecondsExport, getUID } from "../../../system/utils";
import {
  DashboardAggregateType,
  DashboardModuleType,
  ICell,
  IDashboard,
  DashboardOrderType,
  IDataSchema,
  IAcquisitionCell, IEventDataDetail, DashboardSchemaType,
} from "../types";
import { getPalette } from "../../../configuration/configuration";
import { IDictionary, ISelectedNode, SchemaNodeType } from "../../../types";
import { IPoint } from "../../../components/charts/types";
import { formats } from "../../../system/formats";
import { ConverterFn, converters } from "../../../system/converters";

const mockLayout: IDashboard = {
  dashboardID: null,
  name: "New Layout",
  moduleID: 0,
  nodeID: 0,
  nodeType: "",
  userID: 0,
  owner: null,
  isPrivate: true,
  isDefault: false,
  isAcquisition: true,
  isEditable: false,
  customConfig: {
    autoUpdate: {
      enabled: false,
      scan: 60
    },
    range: 1,
    bot: false
  },
  layout: {
    palette: {},
    rows: [
      [
        {
          key: `RC${getUID()}`,
          module: DashboardModuleType.Acquisition,
          title: "Name",
          machine: null,
          variables: [],
          events: []
        }
      ]
    ]
  },
  rtModule: false
};

export const getEmptyDashboard = (): IDashboard => {
  return cloneObject(mockLayout);
};

export const getEmptyCellConfig = (isAcquisition: boolean): ICell | IAcquisitionCell => {
  if (isAcquisition) {

    return {
      key: `RC${getUID()}`,
      module: DashboardModuleType.Acquisition,
      title: "Name",
      machine: null,
      variables: [],
      events: []
    };
  }

  return {
    key: `RC${getUID()}`,
    title: "Name",
    module: DashboardModuleType.Delay,
    chart: "LINE",
    machine: null,
    dateRange: "CURRENT_DAY",
    crew: null,
    shift: null,
    dimension: "Factory Day",
    groups: [ {
      field: "Machine",
      order: DashboardOrderType.Asc
    } ],
    variables:  [ {
      field: "Duration",
      aggregate: DashboardAggregateType.Sum,
      color: getPalette().list[0],
      unit: "h",
      decimals: 1
    } ],
    top: 0,
    filterBy: "DIMENSION",
    orderAsc: false,
    filters: []
  };
};


export class Aggregate {
  private readonly converter: ConverterFn;
  private readonly unit: string | null;
  type = DashboardAggregateType.Count;
  list: number[] = [];

  constructor(type: DashboardAggregateType, converter: ConverterFn, unit: string | null) {
    this.type = type;
    this.converter = converter;
    this.unit = unit;
  }

  add(value: number): void {
    this.list.push(value);
  }

  result(): number {
    switch (this.type) {
      case DashboardAggregateType.Average:
        return this.converter(
          this.list.reduce((previousValue, currentValue) => previousValue + currentValue) / this.list.length,
          this.unit
        );

      case DashboardAggregateType.Count:
        return this.converter(
          this.list.length,
          this.unit);

      case DashboardAggregateType.Minimum:
        return this.converter(
          Math.min(...this.list),
          this.unit);

      case DashboardAggregateType.Maximum:
        return this.converter(
          Math.max(...this.list),
          this.unit);

      case DashboardAggregateType.Sum:
        return this.converter(
          this.list.reduce((previousValue, currentValue) => previousValue + currentValue),
          this.unit);

      default:
        return 0;
    }
  }
}

export const getAggregationText = (type: DashboardAggregateType): string => {
  switch (type) {
    case DashboardAggregateType.Average:
      return "avg";

    case DashboardAggregateType.Count:
      return "count";

    case DashboardAggregateType.Minimum:
      return "min";

    case DashboardAggregateType.Maximum:
      return "max";

    case DashboardAggregateType.Sum:
      return "sum";

    case DashboardAggregateType.AverageByMachine:
      return "sum / machines";

    case DashboardAggregateType.EvalExpression:
      return "Expression";

    default:
      return "---";
  }
};

export const getSchema = (schemas: Record<DashboardSchemaType, any>, module: DashboardModuleType): IDataSchema => {

  if (module === DashboardModuleType.Delay) {
    return schemas[DashboardSchemaType.Delay];
  }

  if (module === DashboardModuleType.Consumption) {
    return schemas[DashboardSchemaType.Consumption];
  }

  return schemas[DashboardSchemaType.Production];
};


export const generateCsv = (cell: ICell, rawData: IDictionary<any>[], schema: IDataSchema): Blob => {
  let content = `${cell.dimension};`;

  cell.groups.forEach(g => {
    content += `${g.field};`;
  });

  cell.variables.forEach(v => {
    content += `${v.field + (v.unit ? ` - [${v.unit}]` : "")};`;
  });

  content += "\n";

  rawData.forEach(row => {
    content += `${formats[schema.dimensions[cell.dimension].formatName](row[cell.dimension])};`;

    cell.groups.forEach(g => {
      content += `${formats[schema.dimensions[g.field].formatName](row[g.field]) ?? "UNDEFINED"};`;
    });

    cell.variables.forEach(v => {
      const value = converters[schema.variables[v.field].converterName](row[v.field], v.unit);
      content += `${v.decimals > 0 ? value.toFixed(v.decimals) : value};`;
    });

    content += "\n";
  });

  return new Blob([ content ], { type: "text/csv" });
};


export const generateAcquisitionCsv = (cell: IAcquisitionCell, rawData: IDictionary<any>): IDictionary<Blob> | null => {

  if (cell.variables.length === 0 && cell.events.length === 0) {
    return null;
  }

  const result: IDictionary<Blob> = {};
  let contentVariables = "Datetime;";

  cell.variables.forEach(v => {
    contentVariables += `${v.field + (v.unit ? ` - [${v.unit}]` : "")};`;
  });

  contentVariables += "\n";

  const preProcess: Record<number, string[]> = {};

  cell.variables.forEach((v, index) => {

    (rawData.variables[v.hash] as IPoint[]).map(p => {
      const x = p.x as number;
      if(!preProcess[x]) {
        preProcess[x] = new Array(cell.variables.length + 1);
        preProcess[x][0] = formatDateTimeWithSecondsExport(x);
      }

      preProcess[x][index + 1] = p.y.toFixed(v.decimals);
    });
  });

  Object.values(preProcess).map(r => {
    contentVariables += `${r.join(";")}\n`;
  });

  result.variables = new Blob([ contentVariables ], { type: "text/csv" });

  cell.events.forEach((e, index) => {
    let header: string[] = [];
    let contentEvents = "";

    (rawData.events[e.hash] as IEventDataDetail[]).map(p => {
      if (header.length === 0){
        header = Object.keys(p.fields);
      }

      contentEvents += `${formatDateTimeWithSecondsExport(p.begin)};${formatDateTimeWithSecondsExport(p.end)};${p.type};${p.title};`;

      header.forEach(h => {
        contentEvents += `${p.fields[h]};`;
      });

      contentEvents += "\n";
    });

    result[`event-${index  + 1}-${e.field}`.toLowerCase()] = new Blob([ "Begin;End;Type;Description;" + header.join(";") + "\n" + contentEvents ], { type: "text/csv" });
  });

  return result;
};

export const getMachinesByNode = (selectedNode: ISelectedNode): ISelectedNode[]  => {
  const list: ISelectedNode[] = [];

  const getMachinesInternal = (node: ISelectedNode): void => {

    if (node.type === SchemaNodeType.Machine) {
      list.push(node);
    }

    if (node.children) {
      node.children.forEach(n => getMachinesInternal(n));
    }
  };

  getMachinesInternal(selectedNode);

  return list;
};
